import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Eye, EyeOff, Mail } from "lucide-react";
import axios from "axios";
import { API, ASSETS, pageVariants, formatApiErrorDetail } from "../components/app/constants";
import { SEO } from "../components/shared/SEO";

export const StartPage = ({ user, onUserCreated, onNavigate }) => {
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState("register"); // "register" | "login" | "forgot"
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const referralCode = searchParams.get("ref");
  const buyParam = searchParams.get("buy");

  // After auth success, either resume checkout intent or go to hub
  const postAuthPath = buyParam ? `/pricing?buy=${buyParam}` : "/hub";

  // If user is already logged in, redirect
  useEffect(() => {
    if (user) {
      onNavigate(postAuthPath);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!name || !email || !password) {
      setError("Please fill in all fields");
      return;
    }
    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    
    setLoading(true);
    setError("");
    
    try {
      const response = await axios.post(`${API}/auth/register`, { 
        name, email, password, referral_code: referralCode || undefined 
      });
      onUserCreated(response.data);
      onNavigate(postAuthPath);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError("Please fill in all fields");
      return;
    }
    
    setLoading(true);
    setError("");
    
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      onUserCreated(response.data);
      onNavigate(postAuthPath);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) {
      setError("Please enter your email");
      return;
    }
    
    setLoading(true);
    setError("");
    
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, { email });
      setSuccessMsg(response.data.message);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const base = window.location.origin + '/start';
    const redirectUrl = buyParam ? `${base}?buy=${buyParam}` : base;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Don't render if user is logged in (will redirect)
  if (user) {
    return null;
  }

  return (
    <motion.div 
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="min-h-screen pt-32 pb-20 px-6"
    >
      <SEO title="Sign Up Free" path="/start" description="Create your free StarFrame AI account. Get 3 free portrait credits instantly. Transform selfies into stunning AI portraits — LinkedIn headshots, magazine covers, fantasy art & more." />
      <div className="max-w-md mx-auto">
        <div className="text-center mb-10">
          <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-4">Welcome</p>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight mb-4">
            {mode === "register" ? "Create Account" : mode === "login" ? "Welcome Back" : "Reset Password"}
          </h1>
          <p className="text-white/60">
            {mode === "register" 
              ? (referralCode ? "You were invited! Sign up to get started." : "Create your account and get 3 free credits instantly.")
              : mode === "login" 
                ? "Sign in to your StarFrame AI account."
                : "Enter your email to receive a reset link."
            }
          </p>
        </div>

        {/* Google Sign-In Button */}
        {mode !== "forgot" && (
          <div className="mb-6">
            <button
              onClick={handleGoogleLogin}
              className="w-full flex items-center justify-center gap-3 py-3.5 bg-white/5 border border-white/20 rounded-xl text-white hover:bg-white/10 transition-all duration-200 font-medium"
              data-testid="google-login-btn"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </button>

            <div className="flex items-center gap-4 my-6">
              <div className="flex-1 h-px bg-white/10" />
              <span className="text-xs uppercase tracking-widest text-white/30">or</span>
              <div className="flex-1 h-px bg-white/10" />
            </div>
          </div>
        )}
        
        {/* Register Form */}
        {mode === "register" && (
          <form onSubmit={handleRegister} className="space-y-5">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-white/40 mb-2 block">Your Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter your name"
                className="input-luxury"
                data-testid="input-name"
              />
            </div>
            
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-white/40 mb-2 block">Email Address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="input-luxury"
                data-testid="input-email"
              />
            </div>

            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-white/40 mb-2 block">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Create a password (6+ characters)"
                  className="input-luxury pr-12"
                  data-testid="input-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60"
                  data-testid="toggle-password"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            
            {error && (
              <p className="text-red-400 text-sm" data-testid="error-message">{error}</p>
            )}
            
            <button 
              type="submit" 
              disabled={loading}
              className="btn-primary w-full flex items-center justify-center gap-2"
              data-testid="submit-button"
            >
              {loading ? (
                <div className="spinner" style={{ width: 20, height: 20, borderWidth: 2 }} />
              ) : (
                <>
                  Create Account
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>
        )}

        {/* Login Form */}
        {mode === "login" && (
          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-white/40 mb-2 block">Email Address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="input-luxury"
                data-testid="input-email"
              />
            </div>

            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-white/40 mb-2 block">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="input-luxury pr-12"
                  data-testid="input-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60"
                  data-testid="toggle-password"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            
            {error && (
              <p className="text-red-400 text-sm" data-testid="error-message">{error}</p>
            )}
            
            <button 
              type="submit" 
              disabled={loading}
              className="btn-primary w-full flex items-center justify-center gap-2"
              data-testid="submit-button"
            >
              {loading ? (
                <div className="spinner" style={{ width: 20, height: 20, borderWidth: 2 }} />
              ) : (
                <>
                  Sign In
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>

            <div className="text-center">
              <button 
                type="button"
                onClick={() => { setMode("forgot"); setError(""); setSuccessMsg(""); }}
                className="text-[#D4AF37] text-sm hover:underline"
                data-testid="forgot-password-link"
              >
                Forgot your password?
              </button>
            </div>
          </form>
        )}

        {/* Forgot Password Form */}
        {mode === "forgot" && (
          <form onSubmit={handleForgotPassword} className="space-y-5">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-white/40 mb-2 block">Email Address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="input-luxury"
                data-testid="input-email"
              />
            </div>
            
            {error && (
              <p className="text-red-400 text-sm" data-testid="error-message">{error}</p>
            )}
            {successMsg && (
              <p className="text-green-400 text-sm" data-testid="success-message">{successMsg}</p>
            )}
            
            <button 
              type="submit" 
              disabled={loading}
              className="btn-primary w-full flex items-center justify-center gap-2"
              data-testid="submit-button"
            >
              {loading ? (
                <div className="spinner" style={{ width: 20, height: 20, borderWidth: 2 }} />
              ) : (
                <>
                  <Mail className="w-4 h-4" />
                  Send Reset Link
                </>
              )}
            </button>
          </form>
        )}
        
        {/* Mode toggle */}
        <p className="text-center text-white/40 text-sm mt-8">
          {mode === "register" ? (
            <>Already have an account?{" "}
              <button onClick={() => { setMode("login"); setError(""); setSuccessMsg(""); }} className="text-[#D4AF37] hover:underline" data-testid="login-link">Sign in</button>
            </>
          ) : (
            <>Don't have an account?{" "}
              <button onClick={() => { setMode("register"); setError(""); setSuccessMsg(""); }} className="text-[#D4AF37] hover:underline" data-testid="register-link">Create one</button>
            </>
          )}
        </p>
      </div>
    </motion.div>
  );
};

